import {WorkbenchProperty} from './handler-structures'

/** Export formats the web shell can trigger via postMessage today. */
export type ViewerExportFormat = 'png' | 'pdf' | 'csv'

export type ViewerExportFormats = Record<ViewerExportFormat, boolean>

const NO_FORMATS: ViewerExportFormats = {png: false, pdf: false, csv: false}

/**
 * Map OBserver {@code workbenchProperties} to the export formats the controller allows.
 *
 * This is only half the answer: several controllers advertise a format the viewer never implements
 * (SAVE_AS_CSV_ALLOWED on the table and HTML viewers, which have no getCSV handler), so the result
 * has to be narrowed by what the iframe reports — see {@link resolveExportFormats}.
 */
export function exportFormatsFromWorkbench(
  properties: readonly string[] | undefined | null
): ViewerExportFormats {
  const set = new Set(properties ?? [])
  return {
    png: set.has(WorkbenchProperty.SAVE_AS_PNG_ALLOWED),
    pdf: set.has(WorkbenchProperty.SAVE_AS_PDF_ALLOWED),
    csv: set.has(WorkbenchProperty.SAVE_AS_CSV_ALLOWED),
  }
}

/**
 * Formats to actually show icons for: allowed by the controller **and** implemented by the viewer.
 *
 * Fails closed — a viewer that never answers the {@code getExportCapabilities} probe gets no icons,
 * so a dead button is never rendered. Viewers that do answer keep their icons in sync automatically
 * as they gain handlers, with no change needed here.
 */
export function resolveExportFormats(
  workbenchProperties: readonly string[] | undefined | null,
  iframeCapabilities: Partial<ViewerExportFormats> | undefined | null
): ViewerExportFormats {
  if (!iframeCapabilities) {
    return NO_FORMATS
  }
  const allowed = exportFormatsFromWorkbench(workbenchProperties)
  return {
    png: allowed.png && iframeCapabilities.png === true,
    pdf: allowed.pdf && iframeCapabilities.pdf === true,
    csv: allowed.csv && iframeCapabilities.csv === true,
  }
}

export type ExportMessageAction = 'getPNG' | 'getPDF' | 'getCSV'

const FORMAT_TO_ACTION: Record<ViewerExportFormat, ExportMessageAction> = {
  png: 'getPNG',
  pdf: 'getPDF',
  csv: 'getCSV',
}

const FORMAT_TO_EXT: Record<ViewerExportFormat, string> = {
  png: 'png',
  pdf: 'pdf',
  // Desktop chart export writes tab-separated *.txt via the CSV channel
  csv: 'txt',
}

/** Sanitize a viewer object name for use as a download filename stem. */
export function sanitizeExportBasename(name: string): string {
  const trimmed = (name || 'export').trim()
  const withoutExt = trimmed.replace(/\.(box|png|pdf|csv|tsv|txt|html)$/i, '')
  return withoutExt.replace(/[^\w.\- ()[\]]+/g, '_').replace(/\s+/g, ' ').trim() || 'export'
}

export function suggestedExportFilename(objectName: string, format: ViewerExportFormat): string {
  return `${sanitizeExportBasename(objectName)}.${FORMAT_TO_EXT[format]}`
}

export function exportActionForFormat(format: ViewerExportFormat): ExportMessageAction {
  return FORMAT_TO_ACTION[format]
}

export function hasAnyExportFormat(formats: ViewerExportFormats): boolean {
  return formats.png || formats.pdf || formats.csv
}

/**
 * Build the parent → iframe export message. The iframe reads {@code filename} to name the download;
 * {@code filepath} is deliberately not used, that field means "absolute path to save to" on the
 * desktop and must never carry a bare name.
 */
export function createExportMessage(
  format: ViewerExportFormat,
  objectId: string,
  objectName: string
) {
  return {
    action: exportActionForFormat(format),
    reqId: objectId,
    filename: suggestedExportFilename(objectName, format),
  }
}

/**
 * Whether an export can be requested right now: there has to be a viewer showing real content, and
 * a mounted iframe to receive the message. Shared by both managers' handleExport.
 */
export function canExport<T extends {current: {contentWindow: unknown} | null}>(
  config: {loading?: boolean; error?: string} | undefined | null,
  iframe: T | undefined | null | false
): iframe is T {
  if (!config || config.loading || config.error) {
    return false
  }
  return !!(iframe && iframe.current?.contentWindow)
}
